/**
 * Up Next Section Component
 *
 * Displays upcoming medications and appointments in a consistent format.
 * Used in both the Care Screen and Treatment Card for unified UX.
 *
 * Features:
 * - Fetches data via useCalendarData hook (single source of truth)
 * - Handles loading, empty, and data states gracefully
 * - Supports medication logging interaction
 * - Consistent styling with Care Screen design
 */

import React, { useMemo } from "react";
import { View, Pressable } from "react-native";
import { Text } from "@louise/ui/Text/Text";
import {
  Calendar,
  MapPin,
  Loader2,
  Syringe,
  Pill,
  Wind,
  CircleDot,
  Info,
  ChevronRight,
} from "lucide-react-native";
import { useCalendarData } from "../useCalendarData";
import {
  CalendarItem,
  getIntakeTypeIcon,
  MedicationIconType,
} from "../calendar.types";
import { toLocalDateString, getToday } from "@src/packages/utils/date";
import {
  formatTime as formatLocalizedTime,
  formatDate as formatLocalizedDate,
} from "@src/packages/i18n/formatters";
import { useLocale } from "@src/packages/i18n/useLocale";
import type { Locale } from "@src/packages/i18n/locale";
import { ClinicalDataStatus } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

// ============================================================================
// Types
// ============================================================================

export interface UpNextSectionProps {
  /** Maximum number of items to show (default: 5) */
  limit?: number;
  /** Whether to show the section header (default: true) */
  showHeader?: boolean;
  /** Custom header text (default: "Up Next") */
  headerText?: string;
  /** Called when medication info button is pressed */
  onMedicationInfoPress?: (item: CalendarItem) => void;
  /** Called when "Did you take it?" is pressed on a due-today medication */
  onMedicationLogPress?: (item: CalendarItem) => void;
  /** Called when an appointment is pressed */
  onAppointmentPress?: (item: CalendarItem) => void;
  /** Custom empty state message */
  emptyMessage?: string;
  /** Optional className for container */
  className?: string;
}

// ============================================================================
// Medication Icon Component
// ============================================================================

function MedicationIcon({
  intakeType,
  size = 18,
  color,
}: {
  intakeType: MedicationIconType;
  size?: number;
  color: string;
}) {
  switch (intakeType) {
    case "injection":
      return <Syringe size={size} color={color} />;
    case "pill":
      return <Pill size={size} color={color} />;
    case "inhaler":
      return <Wind size={size} color={color} />;
    case "drops":
    case "cream":
    case "patch":
    default:
      return <CircleDot size={size} color={color} />;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatTime(isoString: string, locale: Locale): string {
  return formatLocalizedTime(new Date(isoString), locale, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function getDayOfWeek(isoString: string, locale: Locale): string {
  return formatLocalizedDate(new Date(isoString), locale, { weekday: "long" });
}

// ============================================================================
// Card Components
// ============================================================================

/**
 * Medication item card with optional "Did you take it?" action for due-today items.
 */
function MedicationItemCard({
  item,
  showDayLabel = true,
  isDueToday = false,
  onInfoPress,
  onLogPress,
}: {
  item: CalendarItem;
  showDayLabel?: boolean;
  isDueToday?: boolean;
  onInfoPress?: () => void;
  onLogPress?: () => void;
}) {
  const { locale } = useLocale();
  const capturePress = usePressCapture("MedicationItemCard");
  const intakeType = getIntakeTypeIcon(item.meta?.intakeType);
  const time = formatTime(item.startAt, locale);
  const dayOfWeek = getDayOfWeek(item.startAt, locale);

  return (
    <View className="rounded-xl border border-white/10 bg-white/5 p-3.5">
      <View className="flex-row items-start justify-between">
        {/* Left: Icon + Details */}
        <View className="flex-row items-center gap-3">
          {/* Icon tile */}
          <View
            className="h-10 w-10 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${item.color}20` }}
          >
            <MedicationIcon
              intakeType={intakeType}
              size={18}
              color={item.color}
            />
          </View>

          {/* Name and meta */}
          <View>
            <View className="flex-row items-center gap-1.5">
              <Text
                variant="body"
                size="sm"
                className="text-sm font-medium text-slate-100"
              >
                {item.title}
              </Text>
              {onInfoPress && (
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(`More info about ${item.title}`),
                      "medication_info",
                    );
                    onInfoPress();
                  }}
                  className="p-0.5"
                  accessibilityRole="button"
                  accessibilityLabel={`More info about ${item.title}`}
                >
                  <Info size={14} color="#64748B" />
                </Pressable>
              )}
            </View>
            {item.subtitle && (
              <Text variant="caption" className="text-xs text-slate-400">
                {item.subtitle}
              </Text>
            )}
          </View>
        </View>

        {/* Right: Time and day */}
        <View className="items-end">
          <Text
            variant="body"
            size="sm"
            className="text-xs font-medium text-slate-200"
          >
            {time}
          </Text>
          {showDayLabel && (
            <Text
              variant="caption"
              className="mt-0.5 text-[0.65rem] font-medium text-slate-400"
            >
              {dayOfWeek}
            </Text>
          )}
        </View>
      </View>

      {/* Due today action section */}
      {isDueToday && onLogPress && (
        <>
          <View className="my-3 border-t border-white/5" />
          <Pressable
            onPress={() => {
              capturePress("Did you take it?", "log_medication_dose");
              onLogPress();
            }}
            className="flex-row items-center justify-between rounded-lg border border-transparent bg-white/5 px-3 py-2.5"
            accessibilityRole="button"
            accessibilityLabel="Log medication dose"
          >
            <Text
              variant="body"
              size="sm"
              className="text-sm font-medium text-slate-200"
            >
              Did you take it?
            </Text>
            <ChevronRight size={16} color="#64748B" />
          </Pressable>
        </>
      )}
    </View>
  );
}

/**
 * Appointment item card.
 */
function AppointmentItemCard({
  item,
  showDayLabel = true,
  onPress,
}: {
  item: CalendarItem;
  showDayLabel?: boolean;
  onPress?: () => void;
}) {
  const { locale } = useLocale();
  const capturePress = usePressCapture("AppointmentItemCard");
  const time = formatTime(item.startAt, locale);
  const dayOfWeek = getDayOfWeek(item.startAt, locale);

  const content = (
    <View className="rounded-xl border border-white/10 bg-white/5 p-3.5">
      <View className="flex-row items-start justify-between">
        {/* Left: Icon + Details */}
        <View className="flex-row items-center gap-3">
          {/* Icon tile */}
          <View
            className="h-10 w-10 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${item.color}20` }}
          >
            <Calendar size={18} color={item.color} />
          </View>

          {/* Name and meta */}
          <View className="flex-1">
            <Text
              variant="body"
              size="sm"
              className="text-sm font-medium text-slate-100"
            >
              {item.title}
            </Text>
            {item.meta?.location && (
              <View className="mt-0.5 flex-row items-center gap-1">
                <MapPin size={10} color="#64748B" />
                <Text variant="caption" className="text-xs text-slate-400">
                  {item.meta.location}
                </Text>
              </View>
            )}
            {!item.meta?.location && item.meta?.isOnline && (
              <Text variant="caption" className="text-xs text-slate-400">
                Online appointment
              </Text>
            )}
          </View>
        </View>

        {/* Right: Time and day */}
        <View className="items-end">
          <Text
            variant="body"
            size="sm"
            className="text-xs font-medium text-slate-200"
          >
            {time}
          </Text>
          {showDayLabel && (
            <Text
              variant="caption"
              className="mt-0.5 text-[0.65rem] font-medium text-slate-400"
            >
              {dayOfWeek}
            </Text>
          )}
        </View>
      </View>
    </View>
  );

  if (onPress) {
    return (
      <Pressable
        onPress={() => {
          capturePress(pressLabel(item.title), "appointment_row");
          onPress();
        }}
        accessibilityRole="button"
      >
        {content}
      </Pressable>
    );
  }

  return content;
}

/**
 * Renders the appropriate card based on item type.
 */
function UpcomingItemCard({
  item,
  showDayLabel = true,
  todayDateString,
  onMedicationInfoPress,
  onMedicationLogPress,
  onAppointmentPress,
}: {
  item: CalendarItem;
  showDayLabel?: boolean;
  todayDateString?: string;
  onMedicationInfoPress?: (item: CalendarItem) => void;
  onMedicationLogPress?: (item: CalendarItem) => void;
  onAppointmentPress?: (item: CalendarItem) => void;
}) {
  if (item.type === "medication") {
    const itemDate = item.startAt.split("T")[0];
    const isDueToday = todayDateString ? itemDate === todayDateString : false;

    return (
      <MedicationItemCard
        item={item}
        showDayLabel={showDayLabel}
        isDueToday={isDueToday}
        onInfoPress={
          onMedicationInfoPress ? () => onMedicationInfoPress(item) : undefined
        }
        onLogPress={
          onMedicationLogPress ? () => onMedicationLogPress(item) : undefined
        }
      />
    );
  }

  if (item.type === "appointment") {
    return (
      <AppointmentItemCard
        item={item}
        showDayLabel={showDayLabel}
        onPress={
          onAppointmentPress ? () => onAppointmentPress(item) : undefined
        }
      />
    );
  }

  return null;
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Up Next Section - displays upcoming medications and appointments.
 *
 * Uses useCalendarData hook for data fetching, ensuring consistency
 * with the Care Screen implementation.
 */
export function UpNextSection({
  limit = 5,
  showHeader = true,
  headerText = "Up Next",
  onMedicationInfoPress,
  onMedicationLogPress,
  onAppointmentPress,
  emptyMessage = "You have nothing planned yet.",
  className,
}: UpNextSectionProps) {
  const t = useTranslations();
  // Get today's date for comparison
  const today = useMemo(() => getToday(), []);
  const todayString = useMemo(() => toLocalDateString(today), [today]);

  // Calculate date range (current month + next month for upcoming items)
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    return {
      startDate: toLocalDateString(start),
      endDate: toLocalDateString(end),
    };
  }, []);

  // Fetch calendar data
  const { upcomingItems, isLoading, isFetching, availability, refetch } =
    useCalendarData({
      startDate,
      endDate,
    });

  // Filter to only medications and appointments, limit results
  const filteredItems = useMemo(() => {
    return upcomingItems
      .filter(
        (item) => item.type === "medication" || item.type === "appointment",
      )
      .slice(0, limit);
  }, [upcomingItems, limit]);

  return (
    <View className={className}>
      {/* Header */}
      {showHeader && (
        <Text
          variant="caption"
          className="mb-3 px-1 text-[0.65rem] font-bold uppercase tracking-widest text-slate-500"
        >
          {headerText}
        </Text>
      )}

      {/* Content */}
      {availability === "unavailable" ? (
        <ClinicalDataStatus
          state="unavailable"
          appearance="dark"
          title={t("clinical_data.unavailable_title")}
          message={t("clinical_data.unavailable_body")}
          retryLabel={t("clinical_data.retry")}
          retryingLabel={t("clinical_data.retrying")}
          onRetry={refetch}
          isRetrying={isFetching}
          testID="up-next-unavailable"
        />
      ) : isLoading ? (
        // Loading state
        <View className="flex-col items-center rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6">
          <Loader2 size={18} color="#475569" />
        </View>
      ) : filteredItems.length > 0 ? (
        // Items list
        <View className="gap-2">
          {availability === "stale" ? (
            <ClinicalDataStatus
              state="stale"
              appearance="dark"
              message={t("clinical_data.stale_body")}
              retryLabel={t("clinical_data.retry")}
              retryingLabel={t("clinical_data.retrying")}
              onRetry={refetch}
              isRetrying={isFetching}
              testID="up-next-stale"
            />
          ) : null}
          {filteredItems.map((item) => (
            <UpcomingItemCard
              key={item.id}
              item={item}
              showDayLabel={true}
              todayDateString={todayString}
              onMedicationInfoPress={onMedicationInfoPress}
              onMedicationLogPress={onMedicationLogPress}
              onAppointmentPress={onAppointmentPress}
            />
          ))}
        </View>
      ) : (
        // Empty state
        <View className="gap-2">
          {availability === "stale" ? (
            <ClinicalDataStatus
              state="stale"
              appearance="dark"
              message={t("clinical_data.stale_body")}
              retryLabel={t("clinical_data.retry")}
              retryingLabel={t("clinical_data.retrying")}
              onRetry={refetch}
              isRetrying={isFetching}
              testID="up-next-stale"
            />
          ) : null}
          <View className="flex-col items-center rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6">
            <View className="mb-3 h-10 w-10 items-center justify-center rounded-full bg-white/5">
              <Calendar size={18} color="#475569" />
            </View>
            <Text
              variant="body"
              size="sm"
              className="text-xs font-medium text-slate-400"
            >
              {emptyMessage}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
}

// Export card components for advanced usage
export { MedicationItemCard, AppointmentItemCard, UpcomingItemCard };
